/**
 * API Client
 *
 * Handles authenticated requests to the backend Firebase Functions API.
 * Uses Shopify App Bridge for authentication when running as an embedded app.
 */

import type { ApiError } from '../types';

// Base API URL - in production this would be the Firebase Functions URL
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * Custom error class for API errors
 */
export class ApiClientError extends Error {
  status: number;
  data?: ApiError;

  constructor(message: string, status: number, data?: ApiError) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.data = data;
  }
}

/**
 * Get session token for authenticated requests
 * In a real Shopify app, this would use App Bridge to get the token
 */
async function getSessionToken(): Promise<string | null> {
  // Check if we're running in an embedded Shopify app context
  if (typeof window !== 'undefined' && window.shopify) {
    try {
      return await window.shopify.idToken();
    } catch {
      console.warn('Failed to get Shopify session token');
      return null;
    }
  }

  // For development, check for a mock token
  const mockToken = import.meta.env.VITE_MOCK_SESSION_TOKEN;
  if (mockToken) {
    return mockToken;
  }

  return null;
}

/**
 * Build headers for API requests
 */
async function buildHeaders(customHeaders?: HeadersInit): Promise<Headers> {
  const headers = new Headers(customHeaders);

  // Set content type if not already set
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  // Add session token for authentication
  const token = await getSessionToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  return headers;
}

/**
 * Make an authenticated API request
 */
async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const headers = await buildHeaders(options.headers as HeadersInit);

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // Parse response
  let data: T | ApiError;
  const contentType = response.headers.get('content-type');
  if (contentType?.includes('application/json')) {
    data = await response.json();
  } else {
    const text = await response.text();
    data = { error: text } as ApiError;
  }

  // Handle errors
  if (!response.ok) {
    const errorData = data as ApiError;
    throw new ApiClientError(
      errorData.error || errorData.message || 'Request failed',
      response.status,
      errorData
    );
  }

  return data as T;
}

/**
 * API client methods
 */
export const api = {
  /**
   * GET request
   */
  get<T>(endpoint: string, params?: Record<string, string | number | undefined>): Promise<T> {
    let url = endpoint;
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.set(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url = `${endpoint}?${queryString}`;
      }
    }
    return request<T>(url, { method: 'GET' });
  },

  /**
   * POST request
   */
  post<T>(endpoint: string, body?: unknown): Promise<T> {
    return request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  },

  /**
   * PUT request
   */
  put<T>(endpoint: string, body?: unknown): Promise<T> {
    return request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  },

  /**
   * PATCH request
   */
  patch<T>(endpoint: string, body?: unknown): Promise<T> {
    return request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  },

  /**
   * DELETE request
   */
  delete<T>(endpoint: string): Promise<T> {
    return request<T>(endpoint, { method: 'DELETE' });
  },

  /**
   * Upload a file to a signed URL
   */
  async uploadFile(uploadUrl: string, file: File): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
      },
      body: file,
    });

    if (!response.ok) {
      throw new ApiClientError('File upload failed', response.status);
    }
  },
};
